import { Box } from 'src/design/atoms/Box';
import { Card } from 'src/design/atoms/Card';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { Input } from 'src/design/atoms/Input';
import { TextInput } from 'src/design/atoms/TextInput';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  MilestonePhaseDto,
  MilestonePhaseItemDto,
  OrganizationOfficeDto,
} from 'src/api/FiretrolApiClient';
import { useOffices } from 'src/api/queries/organization/useOffices';
import { useMilestoneTemplateDetail } from 'src/api/queries/milestoneTemplates/useMilestoneTemplateDetail';
import {
  useDeleteMilestoneTemplate,
  useUpdateMilestoneTemplate,
} from 'src/api/queries/milestoneTemplates/useMilestoneTemplateMutations';
import { useDeleteMilestoneTemplatePhase } from 'src/api/queries/milestoneTemplates/useMilestoneTemplatePhaseMutations';
import { MilestonePhaseDialog } from 'src/app/dialogs/MilestonePhaseDialog';
import { MilestonePhaseItemDialog } from 'src/app/dialogs/MilestonePhaseItemDialog/MilestonePhaseItemDialog';
import { OfficeSelectorV2 } from 'src/routes/Settings/Milestones/CreateMilestoneTemplate/OfficeSelectorV2Multi';
import { MilestonePhaseItemTable } from 'src/routes/Settings/Milestones/CreateMilestoneTemplate/MilestonePhaseItemTable';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { Header } from 'src/design/organisms/Header';
import { showSuccessToast } from 'src/utils';

export const CreateMilestoneTemplate: React.FC = () => {
  const params: any = useParams();
  const navigate = useNavigate();
  const { isLoading: loadingOffices } = useOffices();
  const templateId: string = params.id;
  const {
    data,
    isLoading: loading,
    isFetching,
  } = useMilestoneTemplateDetail(templateId);
  const updateTemplateMutation = useUpdateMilestoneTemplate();
  const deleteTemplateMutation = useDeleteMilestoneTemplate();
  const deletePhase = useDeleteMilestoneTemplatePhase(templateId);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [itemDialogOpen, setItemDialogOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] =
    React.useState<MilestonePhaseItemDto | null>(null);
  const [copySource, setCopySource] =
    React.useState<MilestonePhaseItemDto | null>(null);
  const [selectedPhase, setSelectedPhase] =
    React.useState<MilestonePhaseDto | null>(null);
  const [name, setName] = React.useState('');
  const [offices, setOffices] = React.useState<OrganizationOfficeDto[]>([]);

  const phases = data?.phases ?? [];
  const items = data?.items ?? [];
  const tname = data?.template.name ?? '';
  const originalOffices = React.useMemo(
    () => data?.template.offices ?? [],
    [data],
  );

  // The name/offices inputs are draft state; reseed them whenever the server
  // copy changes so a silent refetch doesn't strand an edit mid-flight.
  React.useEffect(() => {
    if (!data) return;
    setName(data.template.name);
    setOffices(data.template.offices ?? []);
  }, [data?.template.name, data?.template.offices]);

  const handleCreatePhase = () => {
    setSelectedPhase(null);
    setDialogOpen(true);
  };

  const handleEditPhase = (phase: MilestonePhaseDto) => {
    setSelectedPhase(phase);
    setDialogOpen(true);
  };

  const renderPhases = () => {
    return phases.map((phase) => (
      <Grid.Col span={{ base: 12, md: 3 }} key={phase.id}>
        <Card>
          <Stack>
            <Title order={5}>{phase.title}</Title>
            <Group justify="flex-end">
              <EditActionIcon onClick={() => handleEditPhase(phase)} />
              <DeleteActionIcon
                requiredConfirmation
                confirmationTitle="Remove Phase"
                confirmationText="Are you sure you want to remove this phase?"
                onDelete={() => deletePhase.mutate(phase.id)}
              />
            </Group>
          </Stack>
        </Card>
      </Grid.Col>
    ));
  };

  const updateTemplate = async () => {
    await updateTemplateMutation.mutateAsync({
      templateId,
      request: { id: templateId, name, offices },
    });
    showSuccessToast(`"${name}" updated!`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      updateTemplate();
    }
  };

  const deleteTemplate = async () => {
    await deleteTemplateMutation.mutateAsync(templateId);
    showSuccessToast(`"${name}" deleted!`);
    navigate('/settings/milestones');
  };

  return (
    <>
      <Header
        title="Milestones"
        breadcrumbProps={{
          items: [
            { text: 'Settings', href: '/settings' },
            { text: 'Milestones', href: '/settings/milestones' },
            { text: 'Manage Template' },
          ],
        }}
        helpText="Create and manage milestone templates for project planning."
      />

      <MilestonePhaseDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        templateId={templateId}
        data={selectedPhase}
      />

      <Stack>
        <Paper pos="relative">
          <LoadingOverlay visible={loading || isFetching} />
          <Stack gap="md">
            <Group justify="flex-end" gap="sm">
              <InlineConfirmButton
                color="red"
                confirmButtonProps={{
                  children: 'Confirm Delete',
                }}
                onConfirmClick={deleteTemplate}
              >
                Delete Template
              </InlineConfirmButton>
            </Group>

            <TextInput
              label="Template Name"
              placeholder="Enter template name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
            />

            <Input.Wrapper label="Offices">
              <OfficeSelectorV2
                initialValue={offices}
                onChange={(selectedOffices) => {
                  setOffices(selectedOffices);
                }}
              />
            </Input.Wrapper>

            <Group>
              <SaveButton
                disabled={
                  tname === name &&
                  JSON.stringify(originalOffices) === JSON.stringify(offices)
                }
                onClick={updateTemplate}
              />
            </Group>
          </Stack>
        </Paper>

        <Grid>{renderPhases()}</Grid>
        <Box>
          <CreateButton onClick={handleCreatePhase}>Add Phase</CreateButton>
        </Box>

        <MilestonePhaseItemTable
          templateId={templateId}
          data={items}
          onRowClick={(item) => setSelectedItem(item)}
          onCopyClick={(item) => setCopySource(item)}
          loading={loading}
          hideStatus
          onCreateClick={() => setItemDialogOpen(true)}
        />
      </Stack>

      {selectedItem && (
        <MilestonePhaseItemDialog
          templateId={templateId}
          data={selectedItem}
          opened={selectedItem !== null}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {copySource && (
        <MilestonePhaseItemDialog
          templateId={templateId}
          copyFrom={copySource}
          opened
          onClose={() => setCopySource(null)}
        />
      )}

      <MilestonePhaseItemDialog
        templateId={templateId}
        opened={itemDialogOpen}
        onClose={() => setItemDialogOpen(false)}
      />
    </>
  );
};
